import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import * as debounce from 'lodash.debounce';
import _ from 'underscore';
import QuickSearchResult from './QuickSearchResult';
import { translate } from '../Services/translation';
import {
  query,
  setSearchQuery,
  toggleShowFullForm,
  handleKeyDown,
  handleClickSearch,
} from '../Actions/QuickSearch.action';
import usePrevious from '@react-hook/previous';
import { dataLayerSearch } from '../Services/Gtm.service';

// debouncing function to 200ms so we don't send query request on every key stroke
const debouncedQuery = debounce((dispatch, text) => {
  dispatch(query(text));
  dataLayerSearch(text);
}, 200);

const QuickSearch = (props) => {
  const { query, result, showFullForm, selectedItem } = useSelector(
    (state) => state.quickSearch
  );
  const { isCampaignBannerActive } = props;
  const [lastClickedNode, setLastClickedNode] = useState(null);
  const searchUrl =
    window.__litium.quickSearchUrl + (query.length > 0 ? `?q=${query}` : '');
  const searchContainer = useRef(null);
  const searchInput = useRef(null);
  const dispatch = useDispatch();
  const previousSelectedItem = usePrevious(selectedItem);

  const clickHandler = useCallback((event) => {
    setLastClickedNode(event.target);
  }, []);
  useEffect(() => {
    // listen for click event to hide the search when clicking outside
    window.addEventListener('mousedown', clickHandler);
    return () => window.removeEventListener('mousedown', clickHandler);
  }, [clickHandler]);

  const handleFocus = (event) => {
    // Simulate click if focus comes from tabbing to input field.
    clickHandler(event);
    if (
      result &&
      result.length > 0 &&
      !showFullForm &&
      event.target.value &&
      event.target === searchInput.current
    )
      dispatch(toggleShowFullForm());
  };

  const handleSubmitKeydown = (event) => {
    if (event.key)
      switch (event.key) {
        case 'Tab':
          if (showFullForm && event.shiftKey) dispatch(toggleShowFullForm());
          break;
        default:
          break;
      }
  };

  useEffect(() => {
    // set query value if it is avaialble in the Url
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('q')) {
      const query = urlParams.get('q');
      dispatch(setSearchQuery(query));
      dataLayerSearch(query);
    }
  }, [dispatch]);

  useEffect(() => {
    if (selectedItem !== previousSelectedItem) {
      const el = document.querySelector('.quick-search-result__item--selected');
      el &&
        el.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
          inline: 'nearest',
        });
    }
  }, [selectedItem, previousSelectedItem]);

  let groupedResult = result;
  if (result && result.length > 0) {
    groupedResult = _.groupBy(result, (item) => item.category);
  }

  return (
    <div className="quick-search" role="search" ref={searchContainer}>
      <a
        className="quick-search__link--block quicksearch-icon"
        onClick={(e) => {
          dispatch(toggleShowFullForm());
          setTimeout(() => {
            searchInput.current && searchInput.current.focus();
          }, 0);
        }}
      >
        <i className="quick-search__icon"></i>
        <span className="quick-search__title">
          {translate('general.search')}
        </span>
      </a>
      <div
        className={`quick-search__form ${
          showFullForm ? 'quick-search__form--force-show' : ''
        }`}
        role="search"
      >
        <i
          className="quick-search__icon"
          onClick={(e) => dispatch(toggleShowFullForm())}
        ></i>
        <button
          className="quick-search__submit-button"
          type="submit"
          onClick={(e) => {
            e.preventDefault();
            dispatch(handleClickSearch({ searchUrl }));
          }}
          onKeyDown={(event) => handleSubmitKeydown(event)}
        >
          <i className="quick-search__submit-icon"></i>
          <span className="quick-search__submit-title">
            {translate('general.search')}
          </span>
        </button>
        <input
          id="quick-search-input"
          className="quick-search__input"
          type="search"
          placeholder={translate('general.search')}
          aria-label={translate('general.search.label')}
          autoComplete="off"
          value={decodeURIComponent(query)}
          onChange={(event) => {
            const text = encodeURIComponent(event.target.value);
            dispatch(setSearchQuery(text));
            debouncedQuery(dispatch, text);
          }}
          onKeyDown={(event) => dispatch(handleKeyDown(event, { searchUrl }))}
          ref={searchInput}
          onFocus={(event) => handleFocus(event)}
          onBlur={() => {
            if (
              searchContainer.current &&
              !searchContainer.current.contains(lastClickedNode)
            ) {
              showFullForm && dispatch(toggleShowFullForm());
            }
          }}
        />
        {showFullForm && (
          <QuickSearchResult
            groupedResult={groupedResult}
            selectedItem={selectedItem}
            isCampaignBannerActive={isCampaignBannerActive}
            searchUrl={searchUrl}
          />
        )}
      </div>
    </div>
  );
};

export default QuickSearch;
